import { useEffect, useMemo, useRef } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form'

import {
  computePaymentFormula,
  useDefaultMethodsAutofill,
} from '#/entities/shipment'
import type { PaymentFormulaResult } from '#/entities/shipment'
import { useFormWithZod } from '#/shared/lib/form'
import {
  MANAGE_FIELD_KEY,
  useManageFieldsStore,
} from '#/shared/lib/manage-fields'
import type { PaymentFormData } from './schema'
import { usePaymentSchema } from './schema'

interface UsePaymentEditFormParams {
  defaultValues?: Partial<PaymentFormData>
  onSubmit: (data: PaymentFormData) => void | Promise<void>
}

const EMPTY_DEFAULTS: PaymentFormData = {
  totalTariff: '',
  deposit: '',
  carrierFee: '',
  paymentMethod: '',
  paymentTerms: '',
  balanceMethod: '',
  balanceTime: '',
  balanceTermBegins: '',
  paymentNote: '',
}

function useAutofillDefaultMethods(
  getValues: UseFormGetValues<PaymentFormData>,
  setValue: UseFormSetValue<PaymentFormData>,
) {
  const enabled = !useManageFieldsStore((state) =>
    Boolean(state.hidden[MANAGE_FIELD_KEY.paymentDefaultMethods]),
  )
  useDefaultMethodsAutofill(enabled, getValues, setValue)
}

function parseMoney(value: string): number | null {
  const cleaned = value.replace(/,/g, '').trim()
  if (!cleaned) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function formatMoney(n: number): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function useCarrierFeeAutoCalc({
  tariff,
  deposit,
  manualRef,
  getValues,
  setValue,
}: {
  tariff: string
  deposit: string
  manualRef: { current: boolean }
  getValues: UseFormGetValues<PaymentFormData>
  setValue: UseFormSetValue<PaymentFormData>
}) {
  const prevInputsRef = useRef<{ tariff: string; deposit: string } | null>(null)
  useEffect(() => {
    const prev = prevInputsRef.current
    prevInputsRef.current = { tariff, deposit }
    if (manualRef.current) return
    const t = parseMoney(tariff)
    const d = parseMoney(deposit)
    if (t == null || d == null) return
    // Recalculate only on actual tariff/deposit edits; opening edit mode must
    // not overwrite a saved carrier fee (mount fills it only when empty).
    const inputsChanged =
      prev != null && (prev.tariff !== tariff || prev.deposit !== deposit)
    if (!inputsChanged && getValues('carrierFee').trim() !== '') return
    setValue('carrierFee', formatMoney(t - d), {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [tariff, deposit, manualRef, getValues, setValue])
}

export function usePaymentEditForm({
  defaultValues,
  onSubmit,
}: UsePaymentEditFormParams) {
  const schema = usePaymentSchema()
  const form = useFormWithZod(schema, {
    defaultValues: { ...EMPTY_DEFAULTS, ...defaultValues },
  })

  useAutofillDefaultMethods(form.getValues, form.setValue)

  const carrierManualRef = useRef(false)
  const control = form.control
  const { errors, isDirty } = useFormState({ control })
  const tariff = useWatch({ control, name: 'totalTariff' })
  const deposit = useWatch({ control, name: 'deposit' })
  const carrierFee = useWatch({ control, name: 'carrierFee' })
  const paymentMethod = useWatch({ control, name: 'paymentMethod' })
  const paymentTerms = useWatch({ control, name: 'paymentTerms' })
  const balanceMethod = useWatch({ control, name: 'balanceMethod' })
  const balanceTime = useWatch({ control, name: 'balanceTime' })
  const balanceTermBegins = useWatch({ control, name: 'balanceTermBegins' })

  useCarrierFeeAutoCalc({
    tariff,
    deposit,
    manualRef: carrierManualRef,
    getValues: form.getValues,
    setValue: form.setValue,
  })

  const markCarrierManual = () => {
    carrierManualRef.current = true
  }

  const releaseCarrierManual = () => {
    carrierManualRef.current = false
  }

  const applyTariff = (rate: number) => {
    if (!Number.isFinite(rate)) return
    form.setValue('totalTariff', formatMoney(rate), {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const formula: PaymentFormulaResult = useMemo(
    () =>
      computePaymentFormula({
        totalTariff: tariff,
        deposit,
        carrierPay: carrierFee,
      }),
    [tariff, deposit, carrierFee],
  )

  return {
    register: form.register,
    control: form.control,
    errors,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    isDirty,
    tariff,
    deposit,
    handleSubmit: form.handleSubmit(onSubmit),
    reset: form.reset,
    markCarrierManual,
    releaseCarrierManual,
    applyTariff,
    formula,
    selectValues: {
      paymentMethod,
      paymentTerms,
      balanceMethod,
      balanceTime,
      balanceTermBegins,
    },
  }
}
